import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button } from '../../components/ui';
import { t } from '../../i18n';
import { ApiError, api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';

interface ActiveConflict {
  code: 'active_ticket_exists';
  active: { number: number; code: string; title: string };
}

/**
 * "Tomar" un ticket como el activo. Si el técnico ya trabaja en otro, la API lo rechaza (guía 7.1: se termina el
 * ticket en curso antes de tomar otro) y aquí se pide confirmar que es una urgencia real.
 */
export function TakeButton({ number, code, label, className = '' }: { number: number; /** Para distinguir los botones de una lista. */ code?: string; label?: string; className?: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ActiveConflict['active'] | null>(null);

  async function take(urgent: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api(`/api/tickets/${number}/tomar`, { json: { urgente: urgent } });
      setConflict(null);
      await qc.invalidateQueries({ queryKey: ['tickets'] });
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as Partial<ActiveConflict> | undefined) : undefined;
      if (err instanceof ApiError && err.status === 409 && body?.code === 'active_ticket_exists' && body.active) setConflict(body.active);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {error && <Alert>{error}</Alert>}
      {conflict ? (
        <Alert tone="warning">
          <p>{t('tk.tomar_conflicto', { codigo: conflict.code, titulo: conflict.title })}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button variant="danger" disabled={busy} onClick={() => void take(true)}>
              {t('tk.tomar_urgente')}
            </Button>
            <Button variant="secondary" onClick={() => setConflict(null)}>
              {t('comun.cancelar')}
            </Button>
          </div>
        </Alert>
      ) : (
        <Button variant="secondary" className="px-3 py-1" disabled={busy} aria-label={code ? `${label ?? t('tk.tomar')} ${code}` : undefined} onClick={() => void take(false)}>
          {label ?? t('tk.tomar')}
        </Button>
      )}
    </div>
  );
}
